import React, { useRef, useCallback, useState } from 'react';
import { Scissors, Upload, Download, Save, RefreshCw, Loader2, X, AlertCircle } from 'lucide-react';
import { useBgRemoval } from '../../hooks/useBgRemoval';
import { createStampPreset, type StampPresetItem } from '../../services/api';
import { canSaveToPhotos } from '../../utils/saveImage';
import { RunningLoader } from '@/components/RunningLoader';

/** 棋盤格背景 */
const checkerboardStyle: React.CSSProperties = {
  backgroundImage: [
    'linear-gradient(45deg, #e2e8f0 25%, transparent 25%)',
    'linear-gradient(-45deg, #e2e8f0 25%, transparent 25%)',
    'linear-gradient(45deg, transparent 75%, #e2e8f0 75%)',
    'linear-gradient(-45deg, transparent 75%, #e2e8f0 75%)',
  ].join(', '),
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
};

interface BgRemovalZoneProps {
  onSavedAsPreset?: (preset: StampPresetItem) => void;
}

const BgRemovalZone: React.FC<BgRemovalZoneProps> = ({ onSavedAsPreset }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const bgRemoval = useBgRemoval();
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFile = useCallback((f: File) => {
    setCurrentFile(f);
    setShowSaveInput(false);
    setSaveName('');
    setSaved(false);
    bgRemoval.process(f);
  }, [bgRemoval]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = '';
  }, [handleFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f?.type.startsWith('image/')) handleFile(f);
  }, [handleFile]);

  const handleSaveAsPreset = async () => {
    if (!saveName.trim() || !bgRemoval.resultBlob || !currentFile) return;
    setSaving(true);
    try {
      // 直接用已去背的 resultBlob 儲存，不重複呼叫去背 API
      const removedFile = new File(
        [bgRemoval.resultBlob],
        currentFile.name.replace(/\.[^.]+$/, '.png'),
        { type: 'image/png' },
      );
      const preset = await createStampPreset(saveName.trim(), removedFile);
      onSavedAsPreset?.(preset);
      setShowSaveInput(false);
      setSaveName('');
      setSaved(true);
    } catch (e: any) {
      alert(e.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    bgRemoval.reset();
    setCurrentFile(null);
    setShowSaveInput(false);
    setSaveName('');
    setSaved(false);
  };

  return (
    <section className="bg-white/90 dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
      {/* 標題 */}
      <div className="flex items-center gap-2 mb-4">
        <Scissors size={16} className="text-indigo-500" />
        <h3 className="font-bold text-sm text-slate-800 dark:text-white">圖片去背工具</h3>
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
        將圖片背景移除，可直接下載透明 PNG 或儲存為印章素材。
      </p>

      {/* 閒置狀態：上傳區 */}
      {bgRemoval.status === 'idle' && (
        <div
          onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center gap-2 cursor-pointer transition-all ${
            isDragOver
              ? 'border-indigo-500 bg-indigo-50/50 dark:bg-indigo-900/10'
              : 'border-slate-300 dark:border-slate-600 hover:border-indigo-400 dark:hover:border-indigo-500'
          }`}
        >
          <Upload size={28} className="text-slate-400" />
          <span className="text-sm text-slate-500 dark:text-slate-400 font-medium">點擊或拖曳圖片至此進行去背</span>
          <span className="text-[10px] text-slate-400">支援 JPG / PNG / WebP（10MB 內）</span>
        </div>
      )}

      {/* 處理中 */}
      {bgRemoval.status === 'processing' && (
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="w-24 h-24 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
            {bgRemoval.originalUrl && (
              <img src={bgRemoval.originalUrl} alt="處理中" className="w-full h-full object-contain opacity-60" />
            )}
          </div>
          <div className="text-slate-600 dark:text-slate-300">
            <RunningLoader size="sm" label="去背處理中" />
          </div>
          <span className="text-[10px] text-slate-400">請稍後</span>
          <button
            onClick={bgRemoval.cancel}
            className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1 transition-colors"
          >
            <X size={12} /> 取消
          </button>
        </div>
      )}

      {/* 完成：Before / After */}
      {bgRemoval.status === 'done' && (
        <div className="flex flex-col gap-4">
          <div className="flex gap-4 justify-center">
            {/* 原圖 */}
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-32 h-32 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
                {bgRemoval.originalUrl && (
                  <img src={bgRemoval.originalUrl} alt="原圖" className="w-full h-full object-contain" />
                )}
              </div>
              <span className="text-[10px] text-slate-400 font-medium">原圖</span>
            </div>

            {/* 去背結果 */}
            <div className="flex flex-col items-center gap-1.5">
              <div className="w-32 h-32 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700" style={checkerboardStyle}>
                {bgRemoval.resultUrl && (
                  <img src={bgRemoval.resultUrl} alt="去背結果" className="w-full h-full object-contain" />
                )}
              </div>
              <span className="text-[10px] text-slate-400 font-medium">去背結果</span>
            </div>
          </div>

          {/* 儲存為印章（展開式） */}
          {showSaveInput && (
            <div className="flex gap-2 items-center">
              <input
                autoFocus
                value={saveName}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveAsPreset(); }}
                placeholder="輸入印章名稱..."
                className="flex-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-xs text-slate-700 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <button
                onClick={handleSaveAsPreset}
                disabled={!saveName.trim() || saving}
                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all flex items-center gap-1"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                儲存
              </button>
              <button
                onClick={() => setShowSaveInput(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <X size={14} />
              </button>
            </div>
          )}

          {/* 操作按鈕 */}
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => void bgRemoval.save()}
              className="flex-1 min-w-[120px] py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-950 rounded-xl font-bold text-xs hover:bg-slate-700 dark:hover:bg-slate-100 transition-all flex items-center justify-center gap-1.5"
            >
              <Download size={13} /> {canSaveToPhotos() ? '儲存去背 PNG' : '下載去背 PNG'}
            </button>
            {!showSaveInput && (
              <button
                onClick={() => setShowSaveInput(true)}
                disabled={saved}
                className="flex-1 min-w-[120px] py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-1.5"
              >
                <Save size={13} /> {saved ? '已儲存為印章' : '儲存為印章'}
              </button>
            )}
            <button
              onClick={handleReset}
              className="py-2.5 px-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 rounded-xl text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors flex items-center gap-1.5"
            >
              <RefreshCw size={13} /> 換一張
            </button>
          </div>
        </div>
      )}

      {/* 錯誤 */}
      {bgRemoval.status === 'error' && (
        <div className="flex flex-col items-center gap-3 py-4">
          <AlertCircle size={24} className="text-red-400" />
          <p className="text-sm text-red-500">{bgRemoval.errorMsg || '去背處理失敗'}</p>
          <button
            onClick={handleReset}
            className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 flex items-center gap-1 transition-colors"
          >
            <RefreshCw size={12} /> 重試
          </button>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
    </section>
  );
};

export default BgRemovalZone;
